/**
 * Central helpers for resolving the active bearer token.
 *
 * Two kinds of session can be live:
 *  - A normal customer-user login, persisted in localStorage.
 *  - An "admin hub" impersonation, handed off via the `customer_token` URL
 *    param and persisted in sessionStorage so the browser garbage-collects it
 *    automatically when the tab/window closes.
 *
 * The admin-hub token always takes precedence: when present it is the only
 * session that should be active (see `useAuthStore().loginWithAdminHubToken`).
 */

/** URL query param the admin hub uses to hand off an impersonation token. */
export const ADMIN_HUB_TOKEN_PARAM = 'customer_token'

/**
 * URL query param carrying the customer (company) name. The /auth/login
 * response doesn't include it, so the admin hub passes it alongside the token.
 */
export const ADMIN_HUB_NAME_PARAM = 'customer'

/** sessionStorage key holding the admin-hub session token. */
export const ADMIN_HUB_TOKEN_KEY = 'adminHubToken'

/** sessionStorage key holding the impersonated customer's display name. */
export const ADMIN_HUB_CUSTOMER_NAME_KEY = 'adminHubCustomerName'

/** sessionStorage key holding the impersonated customer's user object. */
export const ADMIN_HUB_USER_KEY = 'adminHubUser'

/** True when an admin-hub impersonation token is present in this tab. */
export function hasAdminHubToken(): boolean {
  return !!sessionStorage.getItem(ADMIN_HUB_TOKEN_KEY)
}

/**
 * The bearer token to send with API requests. The admin-hub token (sessionStorage)
 * wins over a normal login token (localStorage).
 */
export function getAuthToken(): string | null {
  return sessionStorage.getItem(ADMIN_HUB_TOKEN_KEY) || localStorage.getItem('authToken')
}
